import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.includes("Kolkata") || tz.includes("Calcutta")) return "IN";
    if (tz.includes("New_York") || tz.includes("Los_Angeles") || tz.includes("Chicago") || tz.includes("Denver"))
      return "US";
    if (tz.includes("Dubai")) return "AE";
    if (tz.includes("London")) return "GB";
    return null;
  } catch {
    return null;
  }
}

function detectTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  } catch {
    return "Asia/Kolkata";
  }
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null=unknown initially handled by ready flag
  const [ready, setReady] = useState(false);
  const [location, setLocation] = useState(null);
  const [topic, setTopic] = useState(localStorage.getItem("asf_topic") || null);

  const timezone = detectTimezone();

  const loadLocation = useCallback(async (country) => {
    const loc = await api.location(country);
    setLocation(loc);
    localStorage.setItem("asf_country", loc.country);
    return loc;
  }, []);

  useEffect(() => {
    (async () => {
      const saved = localStorage.getItem("asf_country");
      const country = saved || detectCountry() || "DEFAULT";
      try {
        await loadLocation(country);
      } catch {}
      const token = localStorage.getItem("asf_token");
      if (token) {
        try {
          const u = await api.me();
          setUser(u);
          if (u.country) await loadLocation(u.country);
        } catch {
          localStorage.removeItem("asf_token");
        }
      }
      setReady(true);
    })();
  }, [loadLocation]);

  const login = async (data) => {
    const res = await api.login(data);
    localStorage.setItem("asf_token", res.token);
    setUser(res.user);
    if (res.user.country) await loadLocation(res.user.country);
    return res.user;
  };

  const register = async (data) => {
    const country = localStorage.getItem("asf_country") || "IN";
    const res = await api.register({ ...data, country, timezone });
    localStorage.setItem("asf_token", res.token);
    setUser(res.user);
    return res.user;
  };

  const logout = () => {
    localStorage.removeItem("asf_token");
    setUser(null);
  };

  const googleLogin = async (session_id) => {
    const res = await api.googleSession(session_id);
    localStorage.setItem("asf_token", res.token);
    setUser(res.user);
    if (res.user.country) await loadLocation(res.user.country);
    return res.user;
  };

  const changeCountry = async (country) => {
    const loc = await loadLocation(country);
    if (user) {
      try {
        const u = await api.updateProfile({ country, currency: loc.currency });
        setUser(u);
      } catch {}
    }
  };

  const selectTopic = (t) => {
    setTopic(t);
    localStorage.setItem("asf_topic", t);
    api.logEvent(`topic.selected`, { topic: t });
  };

  return (
    <AppContext.Provider
      value={{ user, setUser, ready, location, timezone, topic, selectTopic,
               login, register, googleLogin, logout, changeCountry, loadLocation }}
    >
      {children}
    </AppContext.Provider>
  );
}
